import { NgFor, NgIf } from '@angular/common';
import {
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { PaButtonComponent } from 'src/app/shared/ui-kit/pa-button/pa-button.component';
import { environment } from 'src/environments/environment';
import { ONBOARDING_INTRO } from '../../config/onboarding.config';
import { SupportLinkV2Component } from '../../components/support-link/support-link.component';
import {
  OnboardingQuestionOption,
  OnboardingQuestionView,
  OnboardingScreen,
} from '../../models/onboarding.models';
import { OnboardingAnswerValue } from '../../models/login-v2-api.models';
import { AuthFlowAnalyticsHelper } from '../../helpers/auth-analytics.helper';
import { AuthActionsService } from '../../services/auth-actions.service';
import { LoginV2ApiService } from '../../services/login-v2-api.service';
import { OnboardingService } from '../../services/onboarding.service';

@Component({
  selector: 'app-onboarding-step',
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    RadioButtonModule,
    CheckboxModule,
    PaButtonComponent,
    SupportLinkV2Component,
  ],
  templateUrl: './onboarding-step.component.html',
  styleUrls: [
    '../../styles/_step.styles.scss',
    './onboarding-step.component.scss',
  ],
})
export class OnboardingStepComponent implements OnInit {
  readonly environment = environment;
  readonly intro = ONBOARDING_INTRO;

  readonly questions = signal<OnboardingQuestionView[]>([]);
  readonly screen = signal<OnboardingScreen>('intro');
  readonly loading = signal(true);
  readonly saving = signal(false);

  readonly form = new FormGroup({});

  readonly currentQuestion = computed(() => {
    const screen = this.screen();
    const list = this.questions();
    return typeof screen === 'number' ? (list[screen] ?? null) : null;
  });

  readonly isLastQuestion = computed(() => {
    const screen = this.screen();
    const list = this.questions();
    return typeof screen === 'number' && screen === list.length - 1;
  });

  private readonly actions = inject(AuthActionsService);
  private readonly authAnalytics = inject(AuthFlowAnalyticsHelper);
  private readonly onboarding = inject(OnboardingService);
  private readonly loginApi = inject(LoginV2ApiService);

  ngOnInit(): void {
    void this.loadQuestions();
  }

  controlName(question: OnboardingQuestionView): string {
    return String(question.id);
  }

  isCheckboxChecked(
    question: OnboardingQuestionView,
    optionId: string,
  ): boolean {
    const value = this.form.get(this.controlName(question))?.value;
    return Array.isArray(value) && value.includes(optionId);
  }

  toggleCheckbox(
    question: OnboardingQuestionView,
    optionId: string,
    checked: boolean,
  ): void {
    const control = this.form.get(this.controlName(question));
    if (!control) {
      return;
    }

    const current = Array.isArray(control.value) ? control.value : [];
    const next = checked
      ? [...current, optionId]
      : current.filter((id: string) => id !== optionId);
    control.setValue(next);
  }

  canContinue(question: OnboardingQuestionView): boolean {
    if (this.saving()) {
      return false;
    }
    const value = this.form.get(this.controlName(question))?.value;
    return this.onboarding.hasAnswer(question, value);
  }

  optionInputId(
    question: OnboardingQuestionView,
    option: OnboardingQuestionOption,
  ): string {
    return `${question.id}-${option.id}`;
  }

  startQuestionnaire(): void {
    this.authAnalytics.captureRegistrationOnboardingAnswerClicked();
    this.screen.set(this.firstUnansweredIndex());
    this.trackScreenShown();
  }

  back(): void {
    const screen = this.screen();
    if (typeof screen !== 'number') {
      return;
    }

    if (screen <= 0) {
      this.screen.set('intro');
      this.trackScreenShown();
      return;
    }

    this.screen.set(screen - 1);
    this.trackScreenShown();
  }

  /** Сохраняет ответ. На последнем вопросе оверлей не снимаем до ЛК. */
  continue(): void {
    const question = this.currentQuestion();
    if (!question || !this.canContinue(question) || this.saving()) {
      return;
    }

    const value = this.form.get(this.controlName(question))?.value as
      | string
      | string[];
    if (!this.isPersistableAnswer(value)) {
      return;
    }

    const step = this.onboarding.toAnalyticsStep(this.screen());
    if (step === 'question_1' || step === 'question_2') {
      this.authAnalytics.captureRegistrationOnboardingContinueClicked(step);
    }

    this.saving.set(true);
    void this.loginApi.presentLoading().then(() => {
      this.onboarding.saveAnswer(question.id, value).subscribe({
        next: (ok) => {
          this.saving.set(false);
          if (!ok) {
            this.loginApi.dismissLoading();
            return;
          }
          if (!this.isLastQuestion()) {
            this.loginApi.dismissLoading();
          }
          this.goAfterAnswerSaved();
        },
        error: () => {
          this.loginApi.dismissLoading();
          this.saving.set(false);
        },
      });
    });
  }

  /** Пропуск анкеты — сразу переход в ЛК с оверлеем. */
  skip(): void {
    const step = this.onboarding.toAnalyticsStep(this.screen());
    if (step) {
      this.authAnalytics.captureRegistrationOnboardingSkipClicked(step);
    }
    void this.actions.skipOnboarding();
  }

  private async loadQuestions(): Promise<void> {
    this.loading.set(true);
    await this.loginApi.presentLoading();

    this.onboarding.getQuestions().subscribe({
      next: (items) => {
        this.loginApi.dismissLoading();
        this.questions.set(items);
        this.rebuildForm(items);
        this.restoreScreen(items);
        this.loading.set(false);
        this.trackScreenShown();
      },
      error: () => {
        this.loginApi.dismissLoading();
        this.loading.set(false);
        this.loginApi.toast(
          'Не удалось загрузить вопросы онбординга.',
          'error',
        );
      },
    });
  }

  private restoreScreen(items: OnboardingQuestionView[]): void {
    const anyAnswered = items.some((q) =>
      this.onboarding.hasAnswer(q, q.answer),
    );
    if (!anyAnswered) {
      return;
    }
    this.screen.set(this.firstUnansweredIndex());
  }

  private rebuildForm(items: OnboardingQuestionView[]): void {
    for (const key of Object.keys(this.form.controls)) {
      this.form.removeControl(key);
    }

    for (const question of items) {
      const initial = this.initialControlValue(question);
      const isCheckbox = question.uiType === 'checkbox';
      this.form.addControl(
        this.controlName(question),
        new FormControl(initial, { nonNullable: isCheckbox }),
      );
    }
  }

  private initialControlValue(
    question: OnboardingQuestionView,
  ): string | string[] | null {
    if (question.uiType === 'checkbox') {
      return Array.isArray(question.answer) ? [...question.answer] : [];
    }
    return typeof question.answer === 'string' ? question.answer : null;
  }

  private firstUnansweredIndex(): number {
    const list = this.questions();
    const index = list.findIndex(
      (q) => !this.onboarding.hasAnswer(q, q.answer),
    );
    return index >= 0 ? index : 0;
  }

  /** Следующий вопрос или финиш онбординга. */
  private goAfterAnswerSaved(): void {
    if (this.isLastQuestion()) {
      this.authAnalytics.captureRegistrationOnboardingCompleted();
      void this.actions.finishOnboarding();
      return;
    }

    const screen = this.screen();
    if (typeof screen === 'number') {
      this.screen.set(screen + 1);
      this.trackScreenShown();
    }
  }

  private trackScreenShown(): void {
    const step = this.onboarding.toAnalyticsStep(this.screen());
    if (step) {
      this.authAnalytics.captureRegistrationOnboardingStepShown(step);
    }
  }

  private isPersistableAnswer(
    value: unknown,
  ): value is Exclude<OnboardingAnswerValue, null> {
    if (typeof value === 'string' && value.length > 0) {
      return true;
    }
    return Array.isArray(value) && value.length > 0;
  }
}
